// offline-probe.ts — the two-layer reachability probe.
//
// The OS `online` event often never fires on site Wi-Fi, so while offline mode
// is enabled, the page thinks it is offline (online === false) and the tab is
// visible (document.hidden), the probe asks whether the connection is back:
//
//   - layer 1: a no-cors, credential-less, referrer-less GET of
//     https://<apex>/probe.txt?_=<rand> (host.ts apexHost), aborted after 5 s.
//     It never reads the (opaque) answer: the fetch resolving at all, even on a
//     404, passes; a rejection (DNS, TLS — a captive portal —, network, the
//     abort) is offline. It costs Backlit nothing and tells the apex nothing
//     about the glow or the user. Layer 1 alone never flips `online`;
//   - layer 2, only once layer 1 passed: the glow's own session check, a
//     heartbeat (joining one in flight), bounded at 10 s. Its answer reaches
//     offline.ts onBeat like every beat (a 401 keeps its quarantine and purge);
//     any HTTP answer is the page online again, and the drain runs once with
//     that beat handed over (requestDrain's beat — no second heartbeat).
//
// Schedule: base probeMs (default 15 s, ≥ 5 s, 0 = off), doubling per
// consecutive failed attempt up to 120 s, ±25 %. Layer-2-only (60 s → 5 min)
// where layer 1 can't run: a host with no apex, or once a
// securitypolicyviolation on the document names the probe origin (an author
// CSP; blocked for the page's life). Every 4th consecutive layer-1 failure
// tries layer 2 as well (learned: layer 1 may be broken on its own); a
// layer-1 success resets that count.
//
// Several tabs: layer 2 runs under the Web Lock `backlit-offline-probe`
// (ifAvailable — a tab that finds it held skips layer 2 for that attempt;
// without Web Locks it runs anyway), and the tab it answered posts
// {type:"reachable", at} on the offline channel; a receiving tab goes online
// (offline.ts) without kicking a drain.
//
// Lifecycle: offline.ts attaches the probe once enable() has finished, feeds
// it every online transition (probeNoteOnline) and detaches it in teardown. It
// stops on the online transition, the tab going hidden (→visible only re-arms
// it: the visibility lifecycle beat is itself a layer-2 check) and detach.
// Every timer is cleared then, and unref'd.

import { sendHeartbeatWithUid } from "./heartbeat.js";
import { apexHost } from "./host.js";
import type { OfflineChannel } from "./offline-channel.js";
import { requestDrain, withinValue } from "./offline-sync.js";

export const PROBE_DEFAULT_MS = 15_000;
export const PROBE_MIN_MS = 5_000;
export const PROBE_MAX_MS = 120_000;
// The layer-2-only cadence.
export const PROBE_L2_BASE_MS = 60_000;
export const PROBE_L2_MAX_MS = 300_000;
export const PROBE_L1_TIMEOUT_MS = 5_000;
export const PROBE_L2_TIMEOUT_MS = 10_000;
// Every PROBE_LEARN_EVERY-th consecutive layer-1 failure also tries layer 2.
export const PROBE_LEARN_EVERY = 4;
// MUST-MATCH across SDK builds that may run in another tab of the glow.
export const PROBE_LOCK_NAME = "backlit-offline-probe";

// ProbeHost is offline.ts's side: wanted() is enabled ∧ online === false.
export interface ProbeHost {
  wanted(): boolean;
  probeMs(): number;
  noteOnline(ok: boolean): void;
  channel(): OfflineChannel | null;
}

type Beat = { status: number; uid: string };

export interface ProbeDeps {
  fetch?: (url: string, init: RequestInit) => Promise<unknown>;
  now?: () => number;
  setTimeout?: (fn: () => void, ms: number) => unknown;
  clearTimeout?: (t: unknown) => void;
  locks?: LockManager | null;
  rand?: () => number;
}

let on = true;
let deps: ProbeDeps = {};
let host: ProbeHost | null = null;
let timer: unknown = null;
// gen voids an attempt when the probe stops; life only when it detaches.
let gen = 0;
let life = 0;
let fails = 0;
let l1Fails = 0;
// blocked: an author CSP refused the probe origin (for the page's life).
let blocked = false;
// cancelL1 ends the layer-1 request in flight (its abort timer cleared, the
// fetch aborted, a miss), or is null.
let cancelL1: (() => void) | null = null;
let offs: Array<() => void> = [];

const setT = (fn: () => void, ms: number): unknown => {
  const t = deps.setTimeout ? deps.setTimeout(fn, ms) : setTimeout(fn, ms);
  (t as { unref?: () => void } | null)?.unref?.();
  return t;
};
const clearT = (t: unknown): void => (deps.clearTimeout ?? ((x: unknown) => clearTimeout(x as ReturnType<typeof setTimeout>)))(t);
const nowMs = (): number => (deps.now ?? Date.now)();

const hidden = (): boolean => typeof document !== "undefined" && document.hidden === true;

function locks(): LockManager | null {
  if (deps.locks !== undefined) return deps.locks;
  try {
    return (globalThis as { navigator?: { locks?: LockManager } }).navigator?.locks ?? null;
  } catch {
    return null;
  }
}

// apex is the probe's layer-1 host, or null: layer 2 only.
const apex = (): string | null => (blocked ? null : apexHost());

// probeDelay is the wait before attempt n+1 after n consecutive failures:
// base × 2ⁿ capped at max (never below base), ±25 %.
export function probeDelay(base: number, max: number, n: number, rand: () => number = Math.random): number {
  const d = Math.min(Math.max(base, max), base * 2 ** Math.min(n, 16));
  return d + d * (rand() * 0.5 - 0.25);
}

// disarm stops the probe: the armed timer cleared, an attempt in flight voided
// (its layer-1 request ended).
function disarm(): void {
  gen++;
  if (timer !== null) clearT(timer);
  timer = null;
  cancelL1?.();
}

// updateProbe arms the probe when it should run and isn't armed; stops it
// when it shouldn't.
export function updateProbe(): void {
  const h = host;
  const ms = h ? h.probeMs() : 0;
  if (!on || !h || ms <= 0 || !h.wanted() || hidden()) {
    disarm();
    return;
  }
  if (timer !== null) return;
  const l2 = apex() === null;
  const delay = l2
    ? probeDelay(PROBE_L2_BASE_MS, PROBE_L2_MAX_MS, fails, deps.rand)
    : probeDelay(ms, PROBE_MAX_MS, fails, deps.rand);
  const g = gen;
  timer = setT(() => {
    timer = null;
    if (g === gen) void attempt(g);
  }, delay);
}

// rearmProbe restarts the schedule (enable() with new options).
export function rearmProbe(): void {
  disarm();
  updateProbe();
}

// probeNoteOnline follows offline.ts noteOnline: online stops the probe and
// ends the offline episode (its backoff resets); offline (re)arms it.
export function probeNoteOnline(ok: boolean): void {
  if (ok) {
    fails = l1Fails = 0;
    disarm();
  } else {
    updateProbe();
  }
}

// layer1 resolves whether the apex answered at all (5 s bound).
function layer1(a: string): Promise<boolean> {
  const f = deps.fetch ?? (typeof fetch === "function" ? (fetch as (u: string, i: RequestInit) => Promise<unknown>) : null);
  if (!f) return Promise.resolve(false);
  const ac = typeof AbortController === "function" ? new AbortController() : null;
  const rand = Math.random().toString(36).slice(2) + nowMs().toString(36);
  let t: unknown = null;
  let end!: () => void;
  const late = new Promise<boolean>((r) => {
    end = () => {
      if (t !== null) clearT(t);
      t = null;
      ac?.abort();
      r(false);
    };
    t = setT(end, PROBE_L1_TIMEOUT_MS);
  });
  cancelL1 = end;
  let p: Promise<boolean>;
  try {
    p = Promise.resolve(
      f(`https://${a}/probe.txt?_=${rand}`, {
        mode: "no-cors",
        cache: "no-store",
        credentials: "omit",
        referrerPolicy: "no-referrer",
        ...(ac ? { signal: ac.signal } : {}),
      }),
    ).then(
      () => true,
      () => false,
    );
  } catch {
    p = Promise.resolve(false);
  }
  return Promise.race([p, late]).finally(() => {
    if (t !== null) clearT(t);
    t = null;
    if (cancelL1 === end) cancelL1 = null;
  });
}

// layer2 is the session check under the probe lock: the beat, or null when
// another tab holds the lock.
async function layer2(): Promise<Beat | null> {
  const beat = () => withinValue(sendHeartbeatWithUid(), PROBE_L2_TIMEOUT_MS, { status: 0, uid: "" });
  const l = locks();
  if (!l?.request) return beat();
  try {
    return await l.request(PROBE_LOCK_NAME, { ifAvailable: true }, (lock) => (lock ? beat() : null));
  } catch {
    return beat(); // the lock itself is unusable (e.g. an opaque origin)
  }
}

async function attempt(g: number): Promise<void> {
  const lf = life;
  let tryL2 = true;
  const a = apex();
  if (a !== null) {
    const ok = await layer1(a);
    if (g !== gen) return;
    if (ok) l1Fails = 0;
    else tryL2 = ++l1Fails % PROBE_LEARN_EVERY === 0;
  }
  if (tryL2) {
    const b = await layer2();
    // The beat's own answer has already stopped the probe (onBeat →
    // noteOnline): only a detach voids the hand-off.
    if (lf !== life) return;
    if (b && b.status > 0) {
      const h = host;
      h?.noteOnline(true);
      void requestDrain(false, b);
      h?.channel()?.post({ type: "reachable", at: nowMs() });
      return;
    }
    if (g !== gen) return;
  }
  fails++;
  updateProbe();
}

// onViolation marks the probe origin blocked when an author CSP refused it
// (blockedURI may be cut down to the origin, so origins are compared).
function onViolation(e: Event): void {
  const a = apexHost();
  const uri = (e as { blockedURI?: unknown }).blockedURI;
  if (!a || typeof uri !== "string") return;
  try {
    if (new URL(uri).origin === `https://${a}`) blocked = true;
  } catch {
    /* not a URL (e.g. "inline") */
  }
}

// attachProbe points the probe at h (enable()) and starts it when wanted.
export function attachProbe(h: ProbeHost): void {
  host = h;
  if (offs.length === 0 && typeof document !== "undefined" && typeof document.addEventListener === "function") {
    const d = document;
    const vis = () => updateProbe();
    d.addEventListener("visibilitychange", vis);
    d.addEventListener("securitypolicyviolation", onViolation);
    offs.push(() => {
      d.removeEventListener("visibilitychange", vis);
      d.removeEventListener("securitypolicyviolation", onViolation);
    });
  }
  updateProbe();
}

// detachProbe stops the probe for good (teardown): timer cleared, in-flight
// attempt voided, listeners removed.
export function detachProbe(): void {
  host = null;
  life++;
  disarm();
  fails = l1Fails = 0;
  for (const f of offs.splice(0)) f();
}

// _setProbeForTest turns the probe on or off (the offline test harness turns
// it off) and injects its fetch / clock / timers / locks / jitter.
export function _setProbeForTest(enabled: boolean, d?: ProbeDeps): void {
  on = enabled;
  deps = d ?? {};
  blocked = false;
  updateProbe();
}

// _probeStateForTest is the probe's schedule state.
export function _probeStateForTest() {
  return { armed: timer !== null, fails, l1Fails, blocked };
}
